import { ReactNode } from 'react'

import IconClock from 'src/icons/IconClock'

import { ActiveHour, summarizeActiveHours } from '../RuleActiveHours'

const ICON_CLASS = 'h-3 w-3 fill-none stroke-current'

type HoursTagProps = {
  /** Current active-hours rows — drives the collapsed summary pill. */
  activeHours: ActiveHour[]
  expanded: boolean
  onToggleExpand: () => void
  children?: ReactNode
}

// Mirrors ScheduleTag: a floating "Runs …" pill above a dashed divider, with an
// "Hour details" box below that expands to reveal the editor. With no windows
// the pill reads "Runs all hours" (the rule is unrestricted).
const HoursTag = ({
  activeHours,
  expanded,
  onToggleExpand,
  children,
}: HoursTagProps) => {
  const summary = summarizeActiveHours(activeHours)

  return (
    <div className="relative pt-3">
      {/* Dashed top divider; the floating tag sits centered above it. */}
      <div className="border-t border-dashed border-doubleNickel-gray-300" />

      {/* Floating tag (centered horizontally over the dashed divider). */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-doubleNickel-gray-200 bg-white px-2.5 py-1 text-[10.5px] font-medium text-doubleNickel-gray-900">
          <IconClock className={`${ICON_CLASS} text-doubleNickel-gray-600`} />
          {summary}
        </span>
      </div>

      {/* Hour details / Edit affordance row */}
      <div className="mt-6 rounded-md border border-dashed border-doubleNickel-gray-300 bg-doubleNickel-gray-50 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-doubleNickel-gray-700">
            Hour details
          </span>
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-[11px] font-medium text-doubleNickel-brand-700 hover:text-doubleNickel-brand-800"
          >
            {expanded ? 'Hide hours' : 'Edit hours →'}
          </button>
        </div>

        {expanded ? <div className="mt-3">{children}</div> : null}
      </div>
    </div>
  )
}

export default HoursTag
